import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { api } from "../../api/client.js";
import type { ActiveRunPhase, AskUserQuestionAnswer, CitationContentPart, Message, ProcessItem, ProcessItemStatus, ReferenceItem, RunTrace, SelectedSkill, Session, ThinkingMode } from "../../api/types.js";
import type { UseSessionsState } from "../sessions/useSessions.js";
import type { UseSourceDrawerState } from "../sources/useSourceDrawer.js";
import type { ThreadScrollController } from "./useThreadScroll.js";
import type { UseActiveRunState } from "./useActiveRunState.js";
import { formatAskUserQuestionAnswersForUser } from "./AskUserQuestionCard.js";
import { ActiveRunConflictError, useChatStream } from "./useChatStream.js";
import { useVerificationPolling } from "./useVerificationPolling.js";
import { mergeReferenceEntities } from "../sources/citationSources.js";
import { DEFAULT_SESSION_TITLE } from "../shared/constants.js";

export type RunningChatRun = {
  runId: string;
  abort: AbortController;
  sessionId?: string;
  projectId?: string | null;
};

type ChatRunPhase = "generating" | "finalizing" | "completed" | "failed" | "cancelled";

export type UseChatControllerState = {
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  submitting: boolean;
  running: RunningChatRun | null;
  currentAssistant: Message | null;
  isWelcome: boolean;
  pendingAskMessage: Message | null;
  sendPrompt: (event?: React.FormEvent) => Promise<void>;
  startConversation: (activePrompt: string, askResponse?: { assistantMessageId: string; answers: AskUserQuestionAnswer[] }) => Promise<void>;
  submitAskUserQuestion: (message: Message, answers: AskUserQuestionAnswer[]) => Promise<void>;
  cancelAskUserQuestion: (message: Message) => Promise<void>;
  refreshSessionAfterRun: (sessionId?: string, projectId?: string | null, sessionSummary?: Session) => Promise<void>;
  stopRun: () => Promise<void>;
};

function stringifyDetail(value: unknown) {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function toolProcessLabel(action: "prepare" | "prepared" | "execute" | "output", toolName?: string) {
  const name = String(toolName || "tool").trim() || "tool";
  const lowerName = name.toLowerCase();
  const display = lowerName === "tool" ? "工具" : lowerName === "bash" ? "命令" : lowerName === "read" ? "读取" : name;
  if (action === "prepare") return `准备${display}`;
  if (action === "prepared") return `工具准备完成：${display}`;
  if (action === "execute") return lowerName === "read" ? "读取资料" : `执行${display}`;
  return `工具输出：${display}`;
}

function processItemFromActivity(data: any) {
  if (data.type === "activity") {
    return { kind: "activity", label: data.label, detail: data.detail || "", status: data.status || "active", elapsedMs: data.elapsedMs || 0 };
  }
  if (data.type === "tool_call_started") {
    return { kind: "tool", label: toolProcessLabel("prepare", data.toolName), detail: stringifyDetail(data.input), status: "active", elapsedMs: data.elapsedMs || 0 };
  }
  if (data.type === "tool_call_delta") {
    return { kind: "tool", label: toolProcessLabel("prepare", data.toolName), detail: data.delta || "", status: "active", elapsedMs: data.elapsedMs || 0 };
  }
  if (data.type === "tool_call_finished") {
    return { kind: "tool", label: toolProcessLabel("prepared", data.toolName), detail: stringifyDetail(data.error || data.output), status: data.error ? "error" : "done", elapsedMs: data.elapsedMs || 0 };
  }
  if (data.type === "tool_execution_started") {
    return { kind: "tool", label: toolProcessLabel("execute", data.toolName), detail: stringifyDetail(data.input), status: "active", elapsedMs: data.elapsedMs || 0 };
  }
  if (data.type === "tool_execution_update") {
    return { kind: "tool", label: toolProcessLabel("output", data.toolName), detail: stringifyDetail(data.output), status: "active", elapsedMs: data.elapsedMs || 0 };
  }
  return { kind: "activity", label: "处理过程", detail: stringifyDetail(data), status: "active", elapsedMs: data.elapsedMs || 0 };
}

function appendProcessItem(items: any[], item: any) {
  const next = [...(items || [])];
  const last = next[next.length - 1];
  if (last?.kind === "reasoning" && item.kind === "reasoning") {
    next[next.length - 1] = {
      ...last,
      text: `${last.text || ""}${item.text || ""}`,
      status: item.status || last.status,
      elapsedMs: item.elapsedMs || last.elapsedMs
    };
    return next;
  }
  if (
    last
    && last.kind === item.kind
    && last.kind !== "reasoning"
    && last.label === item.label
    && last.status === item.status
    && (last.detail || "") === (item.detail || "")
  ) {
    next[next.length - 1] = { ...last, elapsedMs: item.elapsedMs || last.elapsedMs };
    return next;
  }
  next.push(item);
  return next;
}

function appendProcessPart(parts: any[], item: any) {
  let next = [...(parts || [])];
  const last = next[next.length - 1];
  const status = item.status === "active"
    ? "running"
    : item.status === "cancelled"
      ? "cancelled"
      : item.status === "error"
        ? "failed"
        : "completed";
  if (last?.type === "process") {
    next[next.length - 1] = { ...last, items: appendProcessItem(last.items || [], item), status };
  } else {
    next = settleProcessParts(next);
    next.push({ type: "process", items: [item], status });
  }
  return next;
}

function settleProcessParts(parts: any[], status: "completed" | "cancelled" | "failed" = "completed") {
  const itemStatus = status === "completed" ? "done" : status === "cancelled" ? "cancelled" : "error";
  return (parts || []).map((part) => part?.type === "process"
    ? {
        ...part,
        status: part.status === "running" ? status : part.status,
        items: (part.items || []).map((item: any) => item.status === "active" ? { ...item, status: itemStatus } : item)
      }
    : part);
}

function settleProcessTimeline(items: any[], status: "completed" | "cancelled" | "failed" = "completed") {
  const itemStatus = status === "completed" ? "done" : status === "cancelled" ? "cancelled" : "error";
  return (items || []).map((item) => item.status === "active" ? { ...item, status: itemStatus } : item);
}

function appendTextPart(parts: any[], text: string) {
  const next = [...(parts || [])];
  const last = next[next.length - 1];
  if (last?.type === "text") {
    next[next.length - 1] = { ...last, text: `${last.text || ""}${text}` };
  } else {
    next.push({ type: "text", text });
  }
  return next;
}

function appendContentPart(parts: any[], contentPart: CitationContentPart) {
  const next = [...(parts || [])];
  const text = contentPart.text || "";
  const last = next[next.length - 1];
  if (last?.type === "text") {
    next[next.length - 1] = {
      ...last,
      text: `${last.text || ""}${text}`,
      contentParts: [...(last.contentParts || [{ type: "text", text: last.text || "" }]), contentPart]
    };
  } else {
    next.push({ type: "text", text, contentParts: [contentPart] });
  }
  return next;
}

function lightweightContentPart(part: CitationContentPart): CitationContentPart {
  if (part.type !== "citation") return part;
  const { source: _source, ...lightweight } = part;
  return lightweight;
}

function processOnlyParts(parts: any[]) {
  const processParts = (parts || []).filter((part) => part?.type === "process");
  const items = processParts
    .flatMap((part) => part.items || []);
  const status = processParts.some((part) => part.status === "running")
    ? "running"
    : processParts.at(-1)?.status || "completed";
  return items.length ? [{ type: "process", items, status }] : [];
}

function resetAssistantSegment(message: Message, segmentId: string, sequence?: number, clearSegmentOutcome = false) {
  const meta = message.meta || {};
  const nextMeta = {
    ...meta,
    activeAssistantSegmentId: segmentId,
    ...(sequence === undefined ? {} : { activeAssistantSegmentSequence: sequence }),
    contentParts: [],
    citationMap: [],
    processParts: meta.runTrace?.version === 1 ? [] : processOnlyParts(meta.processParts || [])
  };
  if (clearSegmentOutcome) {
    delete nextMeta.assistantSegmentOutcome;
  }
  return {
    ...message,
    content: "",
    meta: nextMeta
  };
}

function startAssistantSegment(
  message: Message,
  segmentId: string,
  sequence?: number,
  preservePresentation = false
) {
  if (!preservePresentation) return resetAssistantSegment(message, segmentId, sequence, true);
  const meta = message.meta || {};
  const nextMeta = {
    ...meta,
    activeAssistantSegmentId: segmentId,
    ...(sequence === undefined ? {} : { activeAssistantSegmentSequence: sequence }),
    assistantSegmentClassification: "provisional",
    processParts: meta.runTrace?.version === 1 ? [] : processOnlyParts(meta.processParts || [])
  };
  delete nextMeta.assistantSegmentOutcome;
  return { ...message, meta: nextMeta };
}

function mergeByCitationIndex(items: any[], item: any) {
  if (!item) return items || [];
  const key = item.localIndex ?? item.citationIndex ?? item.index;
  const next = [...(items || [])];
  const index = next.findIndex((candidate) => (candidate.localIndex ?? candidate.citationIndex ?? candidate.index) === key);
  if (index >= 0) next[index] = { ...next[index], ...item };
  else next.push(item);
  return next.sort((a, b) => (a.localIndex ?? a.citationIndex ?? a.index ?? 0) - (b.localIndex ?? b.citationIndex ?? b.index ?? 0));
}

function failedAssistantMeta(meta: Record<string, any> | undefined) {
  const { contentParts: _contentParts, citationMap: _citationMap, sources: _sources, ...rest } = meta || {};
  return {
    ...rest,
    processStatus: "failed",
    runTrace: settleRunTraceMeta(rest, "failed"),
    processTimeline: settleProcessTimeline(rest.processTimeline || [], "failed"),
    processParts: settleProcessParts(rest.processParts || [], "failed")
  };
}

function withoutInlineReferenceSources(meta: Record<string, any> | undefined) {
  const { sources: _sources, referencePool: _referencePool, ...rest } = meta || {};
  return rest;
}

function initialConnectingProcessItem() {
  return {
    itemId: "local:connecting",
    kind: "phase" as const,
    label: "正在连接科研会话",
    detail: { text: "正在建立连接并准备本轮上下文。" },
    status: "running" as const,
    startedElapsedMs: 0
  } satisfies ProcessItem;
}

function initialRunTrace(item: ProcessItem): RunTrace {
  return { version: 1, status: "running", elapsedMs: 0, items: [item] };
}

function upsertRunTraceItem(trace: RunTrace | undefined, item: ProcessItem): RunTrace {
  const current = trace?.version === 1 ? trace : { version: 1 as const, status: "running" as const, elapsedMs: 0, items: [] };
  const items = [...current.items];
  const index = items.findIndex((candidate) => candidate.itemId === item.itemId);
  if (index >= 0) items[index] = item;
  else if (items.length < 100) items.push(item);
  const itemElapsed = item.completedElapsedMs ?? item.startedElapsedMs ?? 0;
  return {
    ...current,
    elapsedMs: Math.max(current.elapsedMs || 0, itemElapsed),
    items
  };
}

function settleRunTraceMeta(meta: Record<string, any>, status: Exclude<ProcessItemStatus, "running">, elapsedMs?: number) {
  const trace = meta.runTrace?.version === 1
    ? meta.runTrace as RunTrace
    : { version: 1 as const, status: "running" as const, elapsedMs: meta.processElapsedMs || 0, items: [] };
  const finalElapsed = Math.max(trace.elapsedMs || 0, elapsedMs || 0);
  const itemStatus = status === "waiting" ? "completed" : status;
  return {
    ...trace,
    status,
    elapsedMs: finalElapsed,
    items: trace.items.map((item) => item.status === "running" || item.status === "waiting"
      ? {
          ...item,
          status: itemStatus,
          completedElapsedMs: finalElapsed,
          durationMs: Math.max(0, finalElapsed - item.startedElapsedMs)
        }
      : item)
  } satisfies RunTrace;
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

const PRE_RUN_STOP_RUN_ID_WAIT_MS = 1200;
const ACTIVE_RUN_PHASES: ActiveRunPhase[] = ["running", "cancelling", "finalizing"];

export function useChatController({
  sessions,
  activeRuns,
  sourceDrawer,
  scroll,
  selectedSkill,
  setSelectedSkill,
  thinkingMode,
  setPlusOpen
}: {
  sessions: UseSessionsState;
  activeRuns: UseActiveRunState;
  sourceDrawer: UseSourceDrawerState;
  scroll: ThreadScrollController;
  selectedSkill: SelectedSkill | null;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkill | null>>;
  thinkingMode: ThinkingMode;
  setPlusOpen: Dispatch<SetStateAction<boolean>>;
}): UseChatControllerState {
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [running, setRunning] = useState<RunningChatRun | null>(null);
  const pendingStopRef = useRef(false);
  const preRunStopRequestedRef = useRef(false);
  const queuedStopActiveRef = useRef(false);
  const activeRunControllerRef = useRef<AbortController | null>(null);
  const acceptedRunIdRef = useRef("");
  const titleRefreshTimersRef = useRef(new Map<string, number>());
  const { startChatStream, stopChatRun } = useChatStream();
  const currentAssistant = [...activeRuns.visibleMessages].reverse().find((message) => message.role === "assistant") || null;
  const isWelcome = !sessions.currentSession && activeRuns.visibleMessages.length === 0;
  const pendingAskMessage = !isWelcome
    ? [...activeRuns.visibleMessages].reverse().find((message) => message.role === "assistant" && message.meta?.askUserQuestion && (message.meta.askUserQuestionStatus || "pending") === "pending") || null
    : null;

  useEffect(() => () => {
    for (const timer of titleRefreshTimersRef.current.values()) window.clearTimeout(timer);
    titleRefreshTimersRef.current.clear();
  }, []);

  function clearTitleRefresh(sessionId: string) {
    const timer = titleRefreshTimersRef.current.get(sessionId);
    if (timer !== undefined) window.clearTimeout(timer);
    titleRefreshTimersRef.current.delete(sessionId);
  }

  function scheduleTitleRefresh(sessionId: string, alreadyUpdated: () => boolean) {
    clearTitleRefresh(sessionId);
    const timer = window.setTimeout(() => {
      titleRefreshTimersRef.current.delete(sessionId);
      if (!alreadyUpdated()) void sessions.loadSessions().catch(() => undefined);
    }, 22_000);
    titleRefreshTimersRef.current.set(sessionId, timer);
  }

  function updateAskMessageMeta(messageId: string, metaPatch: Record<string, unknown>) {
    // 澄清消息可能仍在 live run buffer（例如 beginLiveRun 保留的上一轮消息）,
    // 两个事实源都要打补丁,否则 pending 卡片在提交后不会消失。
    const patch = (old: Message[]) => old.map((message) => message.id === messageId ? { ...message, meta: { ...(message.meta || {}), ...metaPatch } } : message);
    sessions.setMessages(patch);
    activeRuns.setLiveMessages(patch);
  }

  function appendAssistantProcess(assistantId: string, item: any) {
    activeRuns.setLiveEvents((old) => appendProcessItem((old as any[]).slice(-80), item));
    activeRuns.setLiveMessages((old) => old.map((message) => {
      if (message.id !== assistantId) return message;
      const meta = message.meta || {};
      if (meta.runTrace?.version === 1) {
        const status: ProcessItemStatus = item.status === "error"
          ? "failed"
          : item.status === "cancelled"
            ? "cancelled"
            : item.status === "done"
              ? "completed"
              : "running";
        const elapsedMs = Number(item.elapsedMs || meta.runTrace.elapsedMs || 0);
        const processItem: ProcessItem = {
          itemId: item.itemId || `local:${String(item.label || item.kind || "process").replace(/\s+/g, "-")}`,
          kind: item.kind === "tool" ? "tool" : "phase",
          label: item.label || "处理过程",
          ...(item.detail ? { detail: { text: stringifyDetail(item.detail) } } : {}),
          status,
          startedElapsedMs: elapsedMs,
          ...(status === "running" ? {} : { completedElapsedMs: elapsedMs, durationMs: 0 })
        };
        return {
          ...message,
          meta: {
            ...meta,
            runTrace: upsertRunTraceItem(meta.runTrace, processItem),
            processElapsedMs: Math.max(meta.processElapsedMs || 0, elapsedMs),
            thinkingMode
          }
        };
      }
      const processTimeline = appendProcessItem(meta.processTimeline || [], item);
      return {
        ...message,
        meta: {
          ...meta,
          processTimeline,
          processParts: appendProcessPart(meta.processParts || [], item),
          processElapsedMs: item.elapsedMs || meta.processElapsedMs || 0,
          thinkingMode
        }
      };
    }));
  }

  async function stopChatRunWhenReady(runId: string, sessionId: string, assistantId: string) {
    for (let attempt = 0; attempt < 6 && queuedStopActiveRef.current; attempt += 1) {
      try {
        const result: any = await stopChatRun({ runId, sessionId });
        const stopStatus = result?.stop?.status;
        const runStatus = String(result?.run?.status || "");
        if (stopStatus === "cancelled") {
          activeRuns.setPhase("cancelling");
          queuedStopActiveRef.current = false;
          pendingStopRef.current = false;
          return true;
        }
        if (stopStatus === "already_finished" || ["completed", "failed", "cancelled", "finalizing", "cancelling"].includes(runStatus)) {
          if (ACTIVE_RUN_PHASES.includes(runStatus as ActiveRunPhase)) activeRuns.setPhase(runStatus as ActiveRunPhase);
          queuedStopActiveRef.current = false;
          pendingStopRef.current = false;
          void activeRuns.recoverNow();
          return true;
        }
      } catch {
        // Retry: the claim may still be committing on the server.
      }
      await delay(180 + attempt * 120);
    }
    // A terminal SSE callback may have cleared the queue while this helper
    // was sleeping; in that case the terminal handler already owns cleanup.
    if (!queuedStopActiveRef.current) return false;
    queuedStopActiveRef.current = false;
    pendingStopRef.current = false;
    preRunStopRequestedRef.current = false;
    appendRetryableStopNotice(assistantId);
    // The stop window stayed retryable for the whole bounded attempt. Resume
    // from the durable snapshot instead of leaving the stream-owned buffer in
    // a state that ignores every later SSE event and pauses recovery polling.
    activeRuns.detachLiveRunStream();
    void activeRuns.recoverNow();
    return false;
  }

  async function refreshSessionAfterRun(sessionId = sessions.currentSession?.id, projectId = sessions.currentSession?.projectId || null, sessionSummary?: Session) {
    const sessionToRefresh = sessionId
      ? (sessionSummary || (sessions.currentSession?.id === sessionId ? sessions.currentSession : sessions.sessions.find((item) => item.id === sessionId)))
      : null;
    const shouldFollowBottom = !scroll.threadFollowPausedRef.current && scroll.isThreadNearBottom();
    if (sessionToRefresh) await sessions.openSession(sessionToRefresh, { immediate: false, scroll: shouldFollowBottom ? "bottom" : "none" });
    await sessions.loadSessions();
  }

  async function startConversation(activePrompt: string, askResponse?: { assistantMessageId: string; answers: AskUserQuestionAnswer[] }) {
    if (!activePrompt.trim() || activeRunControllerRef.current || running || activeRuns.blockedByActiveRun) return;
    pendingStopRef.current = false;
    preRunStopRequestedRef.current = false;
    queuedStopActiveRef.current = false;
    acceptedRunIdRef.current = "";
    let session = sessions.currentSession;
    const initialTitleSource = session?.titleSource || (session?.title === DEFAULT_SESSION_TITLE ? "default" : "manual");
    const shouldGenerateSessionTitle = !session || initialTitleSource === "default" || initialTitleSource === "provisional";
    let sessionTitleSettled = !shouldGenerateSessionTitle;
    const activeProjectId = session?.projectId || null;
    const activeSkill = selectedSkill;
    const optimisticUser: Message = {
      id: `local-${Date.now()}`,
      role: "user",
      content: activePrompt,
      ...(activeSkill ? { meta: { selectedSkill: activeSkill } } : {})
    };
    const initialProcessItem = initialConnectingProcessItem();
    const assistant: Message = {
      id: `stream-${Date.now()}`,
      role: "assistant",
      content: "",
      meta: {
        citationMap: [],
        activityLog: [],
        processTimeline: [],
        processParts: [],
        runTrace: initialRunTrace(initialProcessItem),
        processStatus: "running",
        runPhase: "generating",
        processElapsedMs: 0,
        thinkingMode
      }
    };
    const controller = new AbortController();
    activeRunControllerRef.current = controller;
    let streamSessionId = session?.id || "";
    let runId = "";
    let streamAccepted = false;
    let streamUsesContentParts = false;
    let streamUsesAssistantSegments = false;
    let streamUsesProcessItems = false;
    let activeAssistantSegmentId = "";
    let terminalAssistantSegmentId = "";
    let activeSegmentContent = "";
    let activeSegmentContentParts: CitationContentPart[] = [];
    let activeSegmentCitationMap: any[] = [];
    let retainedPresentation: {
      sourceSegmentId: string;
      content: string;
      contentParts: CitationContentPart[];
      citationMap: any[];
      commentaryItemId?: string;
      commentaryItem?: ProcessItem;
    } | null = null;
    let runPhase: ChatRunPhase = "generating";
    const matchesRun = (data: any) => {
      // runId is the authoritative identity; sessionId is only consulted when
      // an event carries no runId (legacy fixtures may send a stale sessionId
      // alongside the correct runId).
      if (data?.runId) return !runId || data.runId === runId;
      if (data?.sessionId && streamSessionId) return data.sessionId === streamSessionId;
      return true;
    };
    const applyStructuredProcessItem = (item: ProcessItem) => {
      activeRuns.setLiveEvents((old) => {
        const next = [...(old as any[])];
        const index = next.findIndex((candidate: any) => candidate.itemId === item.itemId);
        if (index >= 0) next[index] = item;
        else next.push(item);
        return next.slice(-100);
      });
      activeRuns.setLiveMessages((old) => old.map((message) => {
        if (message.id !== assistant.id) return message;
        const meta = message.meta || {};
        return {
          ...message,
          meta: {
            ...meta,
            runTrace: upsertRunTraceItem(meta.runTrace, item),
            processElapsedMs: Math.max(meta.processElapsedMs || 0, item.completedElapsedMs ?? item.startedElapsedMs ?? 0)
          }
        };
      }));
    };
    const retainedProcessItemForDisplay = (item: ProcessItem) => {
      if (!retainedPresentation || item.itemId !== retainedPresentation.commentaryItemId) return item;
      retainedPresentation.commentaryItem = item;
      return {
        ...item,
        detail: {
          ...(item.detail || {}),
          text: "该段说明暂时保留在对话区。"
        }
      } satisfies ProcessItem;
    };
    const replaceWithActivePresentation = (restoreCommentary: boolean) => {
      const retained = retainedPresentation;
      if (!retained || !activeSegmentContent) return;
      retainedPresentation = null;
      activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
        ...message,
        content: activeSegmentContent,
        meta: {
          ...(message.meta || {}),
          contentParts: [...activeSegmentContentParts],
          citationMap: [...activeSegmentCitationMap]
        }
      } : message));
      if (restoreCommentary && retained.commentaryItem) {
        applyStructuredProcessItem(retained.commentaryItem);
      }
    };
    const releaseRunUi = () => {
      if (activeRunControllerRef.current !== controller) return;
      activeRunControllerRef.current = null;
      setSubmitting(false);
      setRunning((current) => current?.abort === controller ? null : current);
      scroll.setRunningActive(false);
    };
    const acceptRunUi = () => {
      if (streamAccepted) return;
      streamAccepted = true;
      scroll.setRunningActive(true);
      scroll.resumeThreadFollow();
      activeRuns.beginLiveRun({
        runId: "",
        sessionId: session?.id || null,
        projectId: activeProjectId,
        phase: "running",
        createdAt: new Date().toISOString(),
        messages: [optimisticUser, assistant],
        citationSources: [],
        events: [initialProcessItem],
        abortController: controller,
        source: "stream"
      });
      scroll.scheduleThreadFollow({ force: true });
      sessions.setExpandedActivityMessageId(null);
      setPrompt((current) => (current === activePrompt ? "" : current));
      if (activeSkill) {
        setSelectedSkill((current) => current?.name === activeSkill.name ? null : current);
      }
      setSubmitting(false);
      setRunning({ runId, abort: controller, sessionId: streamSessionId, projectId: activeProjectId });
    };
    const settleLiveTerminal = () => {
      // The buffer stays visible until getRun/session detail confirm the
      // formal message; polling takes over once the stream detaches. Without
      // an accepted run there is nothing to poll, so settle locally.
      if (runId) activeRuns.detachLiveRunStream();
      else activeRuns.markLiveRunSettled();
      releaseRunUi();
      void activeRuns.recoverNow();
    };
    setSubmitting(true);
    setPlusOpen(false);
    try {
      const streamTerminal = await startChatStream(
        {
          sessionId: streamSessionId || undefined,
          projectId: activeProjectId,
          prompt: activePrompt,
          selectedSkill: activeSkill?.name || null,
          thinkingMode,
          askResponse
        },
        {
          onOpened() {
            acceptRunUi();
            if (askResponse) {
              updateAskMessageMeta(askResponse.assistantMessageId, {
                askUserQuestionStatus: "submitted",
                askUserQuestionAnswers: askResponse.answers,
                askUserQuestionSubmittedAt: new Date().toISOString()
              });
            }
          },
          onRunStarted(data) {
            runId = data.runId;
            acceptedRunIdRef.current = runId;
            streamSessionId = data.sessionId || streamSessionId;
            streamUsesContentParts = Boolean(data.contentParts);
            streamUsesAssistantSegments = Boolean(data.assistantSegments && data.liveAssistantSegments);
            streamUsesProcessItems = Boolean(data.processItems && data.liveProcessItems);
            // Legacy fixtures may omit the claim summary fields; fall back to
            // the accepted local timestamp and the known session title.
            activeRuns.bindLiveRun({
              runId,
              sessionId: streamSessionId,
              projectId: data.projectId !== undefined ? data.projectId : activeProjectId,
              sessionTitle: typeof data.sessionTitle === "string" && data.sessionTitle ? data.sessionTitle : (session?.title || DEFAULT_SESSION_TITLE),
              phase: ACTIVE_RUN_PHASES.includes(data.phase) ? data.phase : "running",
              createdAt: typeof data.createdAt === "string" && data.createdAt ? data.createdAt : new Date().toISOString()
            });
            if (streamUsesProcessItems) {
              activeRuns.setLiveMessages((old) => old.map((message) => {
                if (message.id !== assistant.id) return message;
                const meta = message.meta || {};
                const trace = meta.runTrace?.version === 1 ? meta.runTrace as RunTrace : initialRunTrace(initialProcessItem);
                return {
                  ...message,
                  meta: {
                    ...meta,
                    runTrace: { ...trace, items: trace.items.filter((item) => item.itemId !== "local:connecting") }
                  }
                };
              }));
            } else {
              activeRuns.setLiveMessages((old) => old.map((message) => {
                if (message.id !== assistant.id) return message;
                const { runTrace: _runTrace, ...meta } = message.meta || {};
                return {
                  ...message,
                  meta: {
                    ...meta,
                    processTimeline: meta.processTimeline?.length ? meta.processTimeline : [{
                      kind: "activity",
                      label: "科研会话已连接",
                      status: "done",
                      elapsedMs: 0
                    }]
                  }
                };
              }));
            }
            if (!session && streamSessionId) {
              const optimisticSession = {
                id: streamSessionId,
                projectId: activeProjectId,
                title: DEFAULT_SESSION_TITLE,
                titleSource: "default" as const,
                updatedAt: new Date().toISOString()
              };
              session = optimisticSession;
              sessions.setCurrentProjectId(activeProjectId || "");
              sessions.setCurrentSession(optimisticSession);
              sessions.setSessions((old) => old.some((item) => item.id === optimisticSession.id) ? old : [optimisticSession, ...old]);
            }
            if (shouldGenerateSessionTitle && streamSessionId) {
              scheduleTitleRefresh(streamSessionId, () => sessionTitleSettled);
            }
            setRunning({ runId, abort: controller, sessionId: streamSessionId, projectId: activeProjectId });
            if (queuedStopActiveRef.current) {
              void stopChatRunWhenReady(runId, streamSessionId, assistant.id);
            }
          },
          onSessionTitleUpdated(data) {
            const updatedSessionId = String(data.sessionId || "");
            const title = String(data.title || "").trim();
            const titleSource = ["provisional", "generated", "fallback"].includes(String(data.source))
              ? data.source as "provisional" | "generated" | "fallback"
              : "generated";
            if (!updatedSessionId || updatedSessionId !== streamSessionId || !title) return;
            sessionTitleSettled = titleSource !== "provisional";
            if (sessionTitleSettled) clearTitleRefresh(updatedSessionId);
            const applyTitleUpdate = (current: typeof session) => {
              if (!current || current.id !== updatedSessionId || current.titleSource === "manual") return current;
              const currentSource = current.titleSource || (current.title === DEFAULT_SESSION_TITLE ? "default" : "manual");
              if (titleSource === "provisional" && currentSource !== "default") return current;
              if (titleSource !== "provisional" && !["default", "provisional"].includes(currentSource)) return current;
              return { ...current, title, titleSource };
            };
            sessions.setSessions((old) => old.map((item) => (
              applyTitleUpdate(item) || item
            )));
            sessions.setCurrentSession((current) => applyTitleUpdate(current));
            session = applyTitleUpdate(session);
            activeRuns.setSessionTitle(updatedSessionId, title);
          },
          onAssistantSegmentStarted(data) {
            if (!streamUsesAssistantSegments || preRunStopRequestedRef.current || runPhase !== "generating" || !matchesRun(data)) return;
            const segmentId = String(data.segmentId || "");
            if (!segmentId || segmentId === activeAssistantSegmentId) return;
            activeAssistantSegmentId = segmentId;
            terminalAssistantSegmentId = "";
            activeSegmentContent = "";
            activeSegmentContentParts = [];
            activeSegmentCitationMap = [];
            activeRuns.setLiveMessages((old) => old.map((message) => (
              message.id === assistant.id
                ? startAssistantSegment(message, segmentId, data.sequence, Boolean(retainedPresentation))
                : message
            )));
            sourceDrawer.clearCitationTarget();
            scroll.scheduleThreadFollow();
          },
          onAssistantSegmentClassified(data) {
            if (!streamUsesAssistantSegments || preRunStopRequestedRef.current || runPhase !== "generating" || !matchesRun(data)) return;
            const segmentId = String(data.segmentId || "");
            if (!segmentId || segmentId !== activeAssistantSegmentId) return;
            const classification = data.classification === "process" ? "process" : "answer";
            const processItem = data.processItem as ProcessItem | undefined;
            const shouldRetain = classification === "process"
              && data.displayPolicy === "retain_until_replaced"
              && Boolean(activeSegmentContent.trim());
            if (shouldRetain) {
              retainedPresentation = {
                sourceSegmentId: segmentId,
                content: activeSegmentContent,
                contentParts: [...activeSegmentContentParts],
                citationMap: [...activeSegmentCitationMap],
                commentaryItemId: processItem?.itemId,
                commentaryItem: processItem
              };
            }
            const displayedProcessItem = processItem
              ? retainedProcessItemForDisplay(processItem)
              : undefined;
            activeRuns.setLiveMessages((old) => old.map((message) => {
              if (message.id !== assistant.id) return message;
              const meta = message.meta || {};
              const nextMeta = {
                ...meta,
                assistantSegmentClassification: classification,
                ...(displayedProcessItem ? { runTrace: upsertRunTraceItem(meta.runTrace, displayedProcessItem) } : {})
              };
              if (classification !== "process") return { ...message, meta: nextMeta };
              if (shouldRetain) return { ...message, meta: nextMeta };
              return {
                ...message,
                content: "",
                meta: {
                  ...nextMeta,
                  contentParts: [],
                  citationMap: []
                }
              };
            }));
            if (displayedProcessItem) {
              activeRuns.setLiveEvents((old) => {
                const next = [...(old as any[])];
                const index = next.findIndex((candidate: any) => candidate.itemId === displayedProcessItem.itemId);
                if (index >= 0) next[index] = displayedProcessItem;
                else next.push(displayedProcessItem);
                return next.slice(-100);
              });
            }
            if (classification === "process") sourceDrawer.clearCitationTarget();
            scroll.scheduleThreadFollow();
          },
          onProcessItemStarted(data) {
            if (!streamUsesProcessItems || preRunStopRequestedRef.current || runPhase !== "generating" || !data.item || !matchesRun(data)) return;
            applyStructuredProcessItem(retainedProcessItemForDisplay(data.item as ProcessItem));
            scroll.scheduleThreadFollow();
          },
          onProcessItemUpdated(data) {
            if (!streamUsesProcessItems || preRunStopRequestedRef.current || runPhase !== "generating" || !data.item || !matchesRun(data)) return;
            applyStructuredProcessItem(retainedProcessItemForDisplay(data.item as ProcessItem));
            scroll.scheduleThreadFollow();
          },
          onProcessItemCompleted(data) {
            if (!streamUsesProcessItems || preRunStopRequestedRef.current || runPhase !== "generating" || !data.item || !matchesRun(data)) return;
            applyStructuredProcessItem(retainedProcessItemForDisplay(data.item as ProcessItem));
            scroll.scheduleThreadFollow();
          },
          onContentReset(data) {
            if (!streamUsesAssistantSegments || preRunStopRequestedRef.current || !matchesRun(data)) return;
            const segmentId = String(data.segmentId || "");
            if (!segmentId || segmentId !== activeAssistantSegmentId) return;
            activeSegmentContent = "";
            activeSegmentContentParts = [];
            activeSegmentCitationMap = [];
            activeRuns.setLiveMessages((old) => old.map((message) => (
              message.id === assistant.id
                ? retainedPresentation
                  ? startAssistantSegment(message, segmentId, undefined, true)
                  : resetAssistantSegment(message, segmentId)
                : message
            )));
            sourceDrawer.clearCitationTarget();
            scroll.scheduleThreadFollow();
          },
          onAssistantSegmentFinished(data) {
            if (!streamUsesAssistantSegments || preRunStopRequestedRef.current || !matchesRun(data)) return;
            const segmentId = String(data.segmentId || "");
            if (!segmentId || segmentId !== activeAssistantSegmentId) return;
            if (data.outcome === "terminal" || data.outcome === "truncated") {
              if (retainedPresentation && activeSegmentContent) {
                replaceWithActivePresentation(!activeSegmentContent.startsWith(retainedPresentation.content));
              }
              terminalAssistantSegmentId = segmentId;
              runPhase = "finalizing";
              activeRuns.setPhase("finalizing");
              scroll.setRunningActive(false);
              sessions.setExpandedActivityMessageId(null);
            }
            activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
              ...message,
              meta: {
                ...(message.meta || {}),
                assistantSegmentOutcome: data.outcome,
                ...(runPhase === "finalizing" ? {
                  runPhase,
                  processStatus: "completed",
                  runTrace: settleRunTraceMeta(
                    message.meta || {},
                    message.meta?.askUserQuestion && (message.meta?.askUserQuestionStatus || "pending") === "pending" ? "waiting" : "completed",
                    data.elapsedMs
                  ),
                  processTimeline: settleProcessTimeline(message.meta?.processTimeline || []),
                  processParts: settleProcessParts(message.meta?.processParts || [])
                } : {})
              }
            } : message));
          },
          onDelta(data) {
            if (streamUsesContentParts) return;
            if (preRunStopRequestedRef.current || !matchesRun(data)) return;
            activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
              ...message,
              content: message.content + data.text,
              meta: {
                ...(message.meta || {}),
                processParts: streamUsesProcessItems ? [] : appendTextPart(message.meta?.processParts || [], data.text)
              }
            } : message));
            scroll.scheduleThreadFollow();
          },
          onContentPart(data) {
            if (!streamUsesContentParts || preRunStopRequestedRef.current || !matchesRun(data)) return;
            if (streamUsesAssistantSegments && data.segmentId !== activeAssistantSegmentId) return;
            const part = data.part as CitationContentPart | undefined;
            if (!part) return;
            const storedPart = lightweightContentPart(part);
            const citationMapEntry = part.type === "citation" ? part.citationMapEntry : undefined;
            activeSegmentContent += part.text || "";
            activeSegmentContentParts.push(storedPart);
            if (citationMapEntry) {
              activeSegmentCitationMap = mergeByCitationIndex(activeSegmentCitationMap, citationMapEntry);
            }
            const waitingForRetainedPrefix = Boolean(
              retainedPresentation
              && retainedPresentation.content.startsWith(activeSegmentContent)
              && activeSegmentContent.length < retainedPresentation.content.length
            );
            if (!waitingForRetainedPrefix) {
              if (retainedPresentation) {
                replaceWithActivePresentation(!activeSegmentContent.startsWith(retainedPresentation.content));
              } else {
                activeRuns.setLiveMessages((old) => old.map((message) => {
                  if (message.id !== assistant.id) return message;
                  const meta = message.meta || {};
                  return {
                    ...message,
                    content: message.content + (part.text || ""),
                    meta: {
                      ...meta,
                      processParts: streamUsesProcessItems ? [] : appendContentPart(meta.processParts || [], storedPart),
                      contentParts: [...(meta.contentParts || []), storedPart],
                      ...(citationMapEntry ? { citationMap: mergeByCitationIndex(meta.citationMap || [], citationMapEntry) } : {})
                    }
                  };
                }));
              }
            }
            if (part.type === "citation" && part.source) {
              activeRuns.setLiveCitationSources((old) => mergeReferenceEntities(old, part.source as ReferenceItem));
            }
            scroll.scheduleThreadFollow();
          },
          onReasoningDelta(data) {
            if (streamUsesProcessItems || preRunStopRequestedRef.current || runPhase !== "generating" || !matchesRun(data)) return;
            if (streamUsesAssistantSegments && data.segmentId && data.segmentId !== activeAssistantSegmentId) return;
            appendAssistantProcess(assistant.id, { kind: "reasoning", text: data.text, status: "active", elapsedMs: data.elapsedMs || 0 });
            scroll.scheduleThreadFollow();
          },
          onActivity(data) {
            if (streamUsesProcessItems || preRunStopRequestedRef.current || runPhase !== "generating" || !matchesRun(data)) return;
            appendAssistantProcess(assistant.id, processItemFromActivity(data));
            scroll.scheduleThreadFollow();
          },
          onCitation(data) {
            if (preRunStopRequestedRef.current || !matchesRun(data)) return;
            if (streamUsesAssistantSegments && data.segmentId !== activeAssistantSegmentId) return;
            const source = data.source as ReferenceItem | undefined;
            activeRuns.setLiveMessages((old) => old.map((message) => {
              if (message.id !== assistant.id) return message;
              const meta = message.meta || {};
              return {
                ...message,
                meta: {
                  ...meta,
                  citationMap: mergeByCitationIndex(meta.citationMap || [], data.citationMapEntry)
                }
              };
            }));
            if (source) activeRuns.setLiveCitationSources((old) => mergeReferenceEntities(old, source));
          },
          onAskUserQuestion(data) {
            if (
              preRunStopRequestedRef.current
              || runPhase !== "completed"
              || data.runId !== runId
              || data.sessionId !== streamSessionId
              || !data.assistantMessageId
            ) return;
            activeRuns.setLiveMessages((old) => old.map((message) => message.id === data.assistantMessageId ? {
              ...message,
              meta: {
                ...(message.meta || {}),
                askUserQuestion: data.askUserQuestion,
                askUserQuestionStatus: "pending",
                activityStatus: "待确认",
                processStatus: "completed",
                runTrace: settleRunTraceMeta(message.meta || {}, "waiting")
              }
            } : message));
          },
          onCompleted(data) {
            if (preRunStopRequestedRef.current || !matchesRun(data)) return;
            const completedTerminalSegmentId = String(data.terminalSegmentId || "");
            const completedSources = Array.isArray(data.message?.meta?.sources) ? data.message.meta.sources as ReferenceItem[] : [];
            if (completedSources.length) {
              activeRuns.setLiveCitationSources((old) => mergeReferenceEntities(old, completedSources));
            }
            const completedMessage = data.message ? {
              ...data.message,
              meta: withoutInlineReferenceSources(data.message.meta)
            } : null;
            runPhase = "completed";
            activeRuns.setPhase("finalizing");
            activeRuns.setLiveMessages((old) => old.map((message) => {
              if (message.id !== assistant.id) return message;
              if (completedMessage && !streamUsesAssistantSegments) return completedMessage;
              if (!data.message) {
                if (
                  completedTerminalSegmentId
                  && terminalAssistantSegmentId
                  && completedTerminalSegmentId !== terminalAssistantSegmentId
                ) {
                  console.warn("Truth-Seeker completed an unexpected assistant segment", {
                    terminalAssistantSegmentId,
                    completedTerminalSegmentId
                  });
                }
                return {
                  ...message,
                  id: data.messageId || message.id,
                  meta: {
                    ...(message.meta || {}),
                    runId: data.runId || runId,
                    runPhase,
                    processStatus: "completed",
                    processElapsedMs: data.processElapsedMs ?? message.meta?.processElapsedMs,
                    runTrace: settleRunTraceMeta(
                      message.meta || {},
                      message.meta?.askUserQuestion && (message.meta?.askUserQuestionStatus || "pending") === "pending" ? "waiting" : "completed",
                      message.meta?.runTrace?.status === "running" ? data.processElapsedMs : undefined
                    ),
                    processTimeline: settleProcessTimeline(message.meta?.processTimeline || []),
                    processParts: settleProcessParts(message.meta?.processParts || []),
                    ...(data.citationGate ? { citationGate: data.citationGate } : {}),
                    ...(Object.prototype.hasOwnProperty.call(data, "verificationJobId") ? { verificationJobId: data.verificationJobId } : {})
                  }
                };
              }
              const segmentMatches = !completedTerminalSegmentId
                || completedTerminalSegmentId === terminalAssistantSegmentId
                || completedTerminalSegmentId === activeAssistantSegmentId;
              const contentMatches = message.content === completedMessage?.content;
              if (!segmentMatches || !contentMatches) {
                console.warn("Truth-Seeker assistant segment required completed-content reconciliation", {
                  activeAssistantSegmentId,
                  terminalAssistantSegmentId,
                  completedTerminalSegmentId,
                  contentMatches
                });
                return completedMessage || message;
              }
              return { ...completedMessage, content: message.content } as Message;
            }));
            sourceDrawer.setSourceMessageId(data.messageId || data.message?.id || null);
            sourceDrawer.clearCitationTarget();
            settleLiveTerminal();
          },
          onFailed(data) {
            if (!matchesRun(data)) return;
            queuedStopActiveRef.current = false;
            pendingStopRef.current = false;
            runPhase = "failed";
            activeRuns.setLiveMessages((old) => old.map((message) => {
              if (message.id !== assistant.id) return message;
              if (data.phase === "commit" && message.content) {
                return {
                  ...message,
                  meta: {
                    ...(message.meta || {}),
                    runPhase,
                    processStatus: "failed",
                    runTrace: settleRunTraceMeta(message.meta || {}, "failed"),
                    commitError: data.error || "回答保存失败。",
                    processTimeline: settleProcessTimeline(message.meta?.processTimeline || [], "failed"),
                    processParts: settleProcessParts(message.meta?.processParts || [], "failed")
                  }
                };
              }
              // 只有 assistant-segments 流才保留已呈现的正文(例如保留中的工具前分析);
              // 普通 content-parts 流的半截 partial 在失败时仍整体替换为失败提示。
              if (streamUsesAssistantSegments && message.content) {
                return {
                  ...message,
                  meta: {
                    ...(message.meta || {}),
                    runPhase,
                    processStatus: "failed",
                    runTrace: settleRunTraceMeta(message.meta || {}, "failed"),
                    responseError: data.error || "本次处理没有正常完成。",
                    processTimeline: settleProcessTimeline(message.meta?.processTimeline || [], "failed"),
                    processParts: settleProcessParts(message.meta?.processParts || [], "failed")
                  }
                };
              }
              return {
                ...message,
                content: `Truth-Seeker 回复未完成：${data.error}`,
                meta: { ...failedAssistantMeta(message.meta), runPhase }
              };
            }));
            settleLiveTerminal();
          },
          onCancelled(data) {
            if (!matchesRun(data)) return;
            queuedStopActiveRef.current = false;
            pendingStopRef.current = false;
            runPhase = "cancelled";
            if (data.message) {
              activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? data.message : message));
              settleLiveTerminal();
              return;
            }
            activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
              ...message,
              id: data.messageId || message.id,
              meta: {
                ...(message.meta || {}),
                runPhase,
                processStatus: "cancelled",
                processElapsedMs: data.processElapsedMs ?? message.meta?.processElapsedMs,
                runTrace: settleRunTraceMeta(message.meta || {}, "cancelled", data.processElapsedMs),
                processTimeline: settleProcessTimeline(message.meta?.processTimeline || [], "cancelled"),
                processParts: settleProcessParts(message.meta?.processParts || [], "cancelled"),
                ...(data.citationGate ? { citationGate: data.citationGate } : {})
              }
            } : message));
            settleLiveTerminal();
          }
        },
        controller.signal
      );
      if (!streamTerminal && !preRunStopRequestedRef.current) {
        if (runId) {
          // Stream ended without a terminal event: preserve the buffer as
          // finalizing and let run polling recover the formal outcome.
          runPhase = "finalizing";
          activeRuns.setPhase("finalizing");
          activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
            ...message,
            meta: {
              ...(message.meta || {}),
              runPhase,
              processStatus: "completed",
              runTrace: settleRunTraceMeta(message.meta || {}, "completed"),
              processTimeline: settleProcessTimeline(message.meta?.processTimeline || []),
              processParts: settleProcessParts(message.meta?.processParts || [])
            }
          } : message));
          activeRuns.detachLiveRunStream();
          void activeRuns.recoverNow();
        } else {
          // The stream closed before any run was accepted: settle the local
          // buffer as failed and let discovery adopt any server-claimed run.
          runPhase = "failed";
          activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
            ...message,
            content: message.content || "Truth-Seeker 回复未完成：本次处理没有正常完成。",
            meta: { ...failedAssistantMeta(message.meta), runPhase }
          } : message));
          activeRuns.markLiveRunSettled();
          void activeRuns.recoverNow();
        }
      }
    } catch (err) {
      if (err instanceof ActiveRunConflictError) {
        // The claim was rejected: keep the draft, adopt the server's active
        // run, and leave no failed assistant message behind.
        activeRuns.discardPendingLiveRun();
        activeRuns.adoptActiveRun(err.activeRun);
      } else if ((err as Error).name === "AbortError") {
        if (preRunStopRequestedRef.current) {
          if (!runId) queuedStopActiveRef.current = false;
          runPhase = "cancelled";
          activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
            ...message,
            meta: {
              ...(message.meta || {}),
              runPhase: "cancelled",
              processStatus: "cancelled",
              runTrace: settleRunTraceMeta(message.meta || {}, "cancelled"),
              processTimeline: settleProcessTimeline(message.meta?.processTimeline || [], "cancelled"),
              processParts: settleProcessParts(message.meta?.processParts || [], "cancelled")
            }
          } : message));
          if (runId) activeRuns.detachLiveRunStream();
          else activeRuns.markLiveRunSettled();
          void activeRuns.recoverNow();
          void sessions.loadSessions().catch(() => undefined);
        } else if (runId) {
          activeRuns.setPhase("finalizing");
          activeRuns.detachLiveRunStream();
          void activeRuns.recoverNow();
        } else {
          activeRuns.discardPendingLiveRun();
          void activeRuns.recoverNow();
        }
      } else if (runId) {
        activeRuns.setPhase("finalizing");
        activeRuns.detachLiveRunStream();
        void activeRuns.recoverNow();
      } else {
        // The request never produced a run: keep the draft, show the bounded
        // failure locally, and let discovery adopt any server-claimed run.
        runPhase = "failed";
        activeRuns.setLiveMessages((old) => old.map((message) => message.id === assistant.id ? {
          ...message,
          content: "Truth-Seeker 回复未完成：本次处理没有正常完成。",
          meta: { ...failedAssistantMeta(message.meta), runPhase }
        } : message));
        activeRuns.markLiveRunSettled();
        void activeRuns.recoverNow();
      }
    } finally {
      if (!activeRunControllerRef.current || activeRunControllerRef.current === controller) {
        pendingStopRef.current = false;
        preRunStopRequestedRef.current = false;
      }
      releaseRunUi();
    }
  }

  async function sendPrompt(event?: React.FormEvent) {
    event?.preventDefault();
    if (!prompt.trim() || submitting || running || activeRuns.blockedByActiveRun) return;
    await startConversation(prompt);
  }

  async function submitAskUserQuestion(message: Message, answers: AskUserQuestionAnswer[]) {
    if (!sessions.currentSession || submitting || running || activeRuns.blockedByActiveRun) return;
    const userPrompt = formatAskUserQuestionAnswersForUser(answers);
    // The ask card is marked submitted only after the claim is accepted
    // (run_started); a 409 leaves it pending.
    await startConversation(userPrompt, { assistantMessageId: message.id, answers });
  }

  async function cancelAskUserQuestion(message: Message) {
    if (!sessions.currentSession || submitting || running || activeRuns.blockedByActiveRun) return;
    updateAskMessageMeta(message.id, {
      askUserQuestionStatus: "cancelled",
      askUserQuestionCancelledAt: new Date().toISOString()
    });
    try {
      await api("/api/chat/ask-user-question/cancel", {
        method: "POST",
        body: JSON.stringify({ sessionId: sessions.currentSession.id, assistantMessageId: message.id })
      });
    } catch {
      await refreshSessionAfterRun(sessions.currentSession.id, sessions.currentSession.projectId, sessions.currentSession).catch(() => undefined);
    }
  }

  async function stopRun() {
    // A reload-recovered active run has no local stream: stop it through the
    // server-owned summary with the explicit runId + sessionId pair.
    const recoveredTarget = !running && activeRuns.activeRun && activeRuns.activeRun.phase === "running"
      ? { runId: activeRuns.activeRun.runId, sessionId: activeRuns.activeRun.sessionId }
      : null;
    if (!running && !recoveredTarget) return;
    const stoppedRun = running;
    const stopRunId = stoppedRun?.runId || recoveredTarget?.runId || "";
    const stopSessionId = stoppedRun?.sessionId || activeRuns.liveRun?.sessionId || recoveredTarget?.sessionId || "";
    if (stopRunId && stopSessionId) {
      if (pendingStopRef.current) return;
      pendingStopRef.current = true;
      try {
        const result = await stopChatRun({ runId: stopRunId, sessionId: stopSessionId });
        const stopStatus = result?.stop?.status;
        if (stopStatus === "cancelled") {
          // Keep the buffer and the global send block; SSE or polling will
          // recover the persisted cancelled message.
          activeRuns.setPhase("cancelling");
          return;
        }
        if (stopStatus === "already_finished") {
          activeRuns.setPhase("finalizing");
          void activeRuns.recoverNow();
          return;
        }
        appendRetryableStopNotice();
      } catch {
        appendRetryableStopNotice();
      } finally {
        pendingStopRef.current = false;
      }
      return;
    }
    if (!stoppedRun) return;
    // The run is not accepted yet: queue the stop until both IDs are known.
    pendingStopRef.current = true;
    preRunStopRequestedRef.current = true;
    queuedStopActiveRef.current = true;
    window.setTimeout(() => {
      if (queuedStopActiveRef.current && !acceptedRunIdRef.current) stoppedRun.abort.abort();
    }, PRE_RUN_STOP_RUN_ID_WAIT_MS);
    if (currentAssistant?.id) {
      appendAssistantProcess(currentAssistant.id, {
        kind: "activity",
        label: "正在停止本轮回答",
        detail: "等待后端返回运行编号后发送停止请求。",
        status: "active",
        elapsedMs: 0
      });
    }
  }

  function appendRetryableStopNotice(assistantId?: string) {
    const resolvedAssistantId = assistantId
      || activeRuns.liveRun?.messages.find((message) => message.role === "assistant")?.id;
    if (!resolvedAssistantId) return;
    appendAssistantProcess(resolvedAssistantId, {
      kind: "activity",
      label: "停止请求暂时不可用",
      detail: "运行仍在处理中，可稍后重试停止。",
      status: "active",
      elapsedMs: 0
    });
  }

  function applyVerificationJob(messageId: string, items: any[], status: string) {
    const byCitationIndex = new Map(items.map((item) => [Number(item.citationIndex), item]));
    const byPoolId = new Map(items.filter((item) => item.poolId).map((item) => [String(item.poolId), item]));
    sessions.setMessages((old) => old.map((message) => {
      if (message.id !== messageId) return message;
      const meta = message.meta || {};
      const verificationFor = (value: any) => byCitationIndex.get(Number(value?.localIndex ?? value?.citationIndex ?? value?.index))
        || byPoolId.get(String(value?.poolId || ""));
      const citationMap = (meta.citationMap || []).map((entry: any) => {
        const verification = verificationFor(entry);
        return verification ? { ...entry, verification } : entry;
      });
      const citationMapByLocalIndex = new Map(citationMap.map((entry: any) => [Number(entry.localIndex), entry]));
      return {
        ...message,
        meta: {
          ...meta,
          verificationStatus: status,
          citationMap,
          contentParts: (meta.contentParts || []).map((part: any) => {
            if (part.type !== "citation") return part;
            const verification = verificationFor(part);
            if (!verification) return part;
            const entry = {
              ...(citationMapByLocalIndex.get(Number(part.localIndex)) || {}),
              ...(part.citationMapEntry || {})
            } as any;
            return {
              ...part,
              citationMapEntry: {
                ...entry,
                localIndex: entry.localIndex ?? part.localIndex,
                poolId: entry.poolId || part.poolId,
                sourceType: entry.sourceType ?? part.sourceType,
                referenceItemId: entry.referenceItemId ?? part.referenceItemId ?? null,
                webSourceId: entry.webSourceId ?? part.webSourceId ?? null,
                evidenceSnippetId: entry.evidenceSnippetId ?? part.evidenceSnippetId ?? null,
                verification
              }
            };
          })
        }
      };
    }));
  }

  useVerificationPolling({ currentAssistant, onJobUpdate: applyVerificationJob });

  return {
    prompt,
    setPrompt,
    submitting,
    running,
    currentAssistant,
    isWelcome,
    pendingAskMessage,
    sendPrompt,
    startConversation,
    submitAskUserQuestion,
    cancelAskUserQuestion,
    refreshSessionAfterRun,
    stopRun
  };
}
